import React, {useState} from "react";

interface ErrorCode {
    code: string;
    message: string;
    description: string;
    category: string;
}

const httpErrorCodes: ErrorCode[] = [
    // 1xx Informational
    {
        code: "100",
        message: "Continue",
        description: "The server has received the request headers and the client should proceed to send the request body",
        category: "Informational"
    },
    {
        code: "101",
        message: "Switching Protocols",
        description: "The server agrees to switch protocols as requested by the client",
        category: "Informational"
    },
    {
        code: "102",
        message: "Processing",
        description: "The server has received and is processing the request, but no response is available yet",
        category: "Informational"
    },
    {
        code: "103",
        message: "Early Hints",
        description: "Used to return some response headers before final HTTP message",
        category: "Informational"
    },

    // 2xx Success
    {
        code: "200",
        message: "OK",
        description: "The request has succeeded",
        category: "Success"
    },
    {
        code: "201",
        message: "Created",
        description: "The request has succeeded and a new resource has been created",
        category: "Success"
    },
    {
        code: "202",
        message: "Accepted",
        description: "The request has been accepted for processing but has not been completed",
        category: "Success"
    },
    {
        code: "203",
        message: "Non-Authoritative Information",
        description: "The returned metadata is not exactly the same as is available from the origin server",
        category: "Success"
    },
    {
        code: "204",
        message: "No Content",
        description: "The server successfully processed the request but is not returning any content",
        category: "Success"
    },
    {
        code: "205",
        message: "Reset Content",
        description: "The server successfully processed the request, asks that the client reset its document view",
        category: "Success"
    },
    {
        code: "206",
        message: "Partial Content",
        description: "The server is delivering only part of the resource due to a range header sent by the client",
        category: "Success"
    },
    {
        code: "207",
        message: "Multi-Status",
        description: "The message body contains multiple status responses in XML (used in WebDAV)",
        category: "Success"
    },
    {
        code: "208",
        message: "Already Reported",
        description: "The members of a DAV binding have already been enumerated in a preceding part of the response",
        category: "Success"
    },
    {
        code: "226",
        message: "IM Used",
        description: "The server has fulfilled a GET request for the resource with a representation of the result of one or more instance-manipulations",
        category: "Success"
    },

    // 3xx Redirection
    {
        code: "300",
        message: "Multiple Choices",
        description: "The requested resource has multiple representations available",
        category: "Redirection"
    },
    {
        code: "301",
        message: "Moved Permanently",
        description: "The requested resource has been permanently moved to a new URL",
        category: "Redirection"
    },
    {
        code: "302",
        message: "Found",
        description: "The requested resource temporarily resides under a different URL",
        category: "Redirection"
    },
    {
        code: "303",
        message: "See Other",
        description: "The response to the request can be found under a different URL",
        category: "Redirection"
    },
    {
        code: "304",
        message: "Not Modified",
        description: "The resource has not been modified since the last request",
        category: "Redirection"
    },
    {
        code: "307",
        message: "Temporary Redirect",
        description: "The requested resource temporarily resides under a different URL (preserves request method)",
        category: "Redirection"
    },
    {
        code: "308",
        message: "Permanent Redirect",
        description: "The requested resource has been permanently moved to another URL (preserves request method)",
        category: "Redirection"
    },

    // 4xx Client Errors
    {
        code: "400",
        message: "Bad Request",
        description: "The server cannot process the request due to client error",
        category: "Client Error"
    },
    {
        code: "401",
        message: "Unauthorized",
        description: "Authentication is required and has failed or not been provided",
        category: "Client Error"
    },
    {
        code: "402",
        message: "Payment Required",
        description: "Reserved for future use. Originally intended for digital payment systems",
        category: "Client Error"
    },
    {
        code: "403",
        message: "Forbidden",
        description: "The server understood the request but refuses to authorize it",
        category: "Client Error"
    },
    {
        code: "404",
        message: "Not Found",
        description: "The requested resource could not be found on the server",
        category: "Client Error"
    },
    {
        code: "405",
        message: "Method Not Allowed",
        description: "The request method is not supported for the requested resource",
        category: "Client Error"
    },
    {
        code: "406",
        message: "Not Acceptable",
        description: "The requested resource is capable of generating only content not acceptable according to the Accept headers sent",
        category: "Client Error"
    },
    {
        code: "407",
        message: "Proxy Authentication Required",
        description: "Authentication with the proxy is required before serving the request",
        category: "Client Error"
    },
    {
        code: "408",
        message: "Request Timeout",
        description: "The server timed out waiting for the request",
        category: "Client Error"
    },
    {
        code: "409",
        message: "Conflict",
        description: "The request conflicts with the current state of the server",
        category: "Client Error"
    },
    {
        code: "410",
        message: "Gone",
        description: "The requested resource is no longer available and will not be available again",
        category: "Client Error"
    },
    {
        code: "411",
        message: "Length Required",
        description: "The request did not specify the length of its content as required by the resource",
        category: "Client Error"
    },
    {
        code: "412",
        message: "Precondition Failed",
        description: "Server does not meet one of the preconditions specified in the request headers",
        category: "Client Error"
    },
    {
        code: "413",
        message: "Payload Too Large",
        description: "The request is larger than the server is willing or able to process",
        category: "Client Error"
    },
    {
        code: "414",
        message: "URI Too Long",
        description: "The URI provided was too long for the server to process",
        category: "Client Error"
    },
    {
        code: "415",
        message: "Unsupported Media Type",
        description: "The request entity has a media type which the server does not support",
        category: "Client Error"
    },
    {
        code: "416",
        message: "Range Not Satisfiable",
        description: "The client has asked for a portion of the file but the server cannot supply that portion",
        category: "Client Error"
    },
    {
        code: "417",
        message: "Expectation Failed",
        description: "The server cannot meet the requirements of the Expect request-header field",
        category: "Client Error"
    },
    {
        code: "418",
        message: "I'm a teapot",
        description: "The server refuses to brew coffee because it is a teapot (April Fools' joke)",
        category: "Client Error"
    },
    {
        code: "422",
        message: "Unprocessable Entity",
        description: "The request was well-formed but was unable to be followed due to semantic errors",
        category: "Client Error"
    },
    {
        code: "425",
        message: "Too Early",
        description: "The server is unwilling to risk processing a request that might be replayed",
        category: "Client Error"
    },
    {
        code: "426",
        message: "Upgrade Required",
        description: "The client should switch to a different protocol",
        category: "Client Error"
    },
    {
        code: "428",
        message: "Precondition Required",
        description: "The origin server requires the request to be conditional",
        category: "Client Error"
    },
    {
        code: "429",
        message: "Too Many Requests",
        description: "The user has sent too many requests in a given amount of time",
        category: "Client Error"
    },
    {
        code: "431",
        message: "Request Header Fields Too Large",
        description: "The server is unwilling to process the request because its header fields are too large",
        category: "Client Error"
    },
    {
        code: "451",
        message: "Unavailable For Legal Reasons",
        description: "Resource access is denied for legal reasons",
        category: "Client Error"
    },

    // 5xx Server Errors
    {
        code: "500",
        message: "Internal Server Error",
        description: "A generic error occurred on the server",
        category: "Server Error"
    },
    {
        code: "501",
        message: "Not Implemented",
        description: "The server does not support the functionality required to fulfill the request",
        category: "Server Error"
    },
    {
        code: "502",
        message: "Bad Gateway",
        description: "The server received an invalid response from the upstream server",
        category: "Server Error"
    },
    {
        code: "503",
        message: "Service Unavailable",
        description: "The server is temporarily unable to handle the request",
        category: "Server Error"
    },
    {
        code: "504",
        message: "Gateway Timeout",
        description: "The upstream server failed to send a request in time",
        category: "Server Error"
    },
    {
        code: "505",
        message: "HTTP Version Not Supported",
        description: "The server does not support the HTTP protocol version used in the request",
        category: "Server Error"
    },
    {
        code: "506",
        message: "Variant Also Negotiates",
        description: "Transparent content negotiation for the request results in a circular reference",
        category: "Server Error"
    },
    {
        code: "507",
        message: "Insufficient Storage",
        description: "The server is unable to store the representation needed to complete the request",
        category: "Server Error"
    },
    {
        code: "508",
        message: "Loop Detected",
        description: "The server detected an infinite loop while processing the request",
        category: "Server Error"
    },
    {
        code: "510",
        message: "Not Extended",
        description: "Further extensions to the request are required for the server to fulfill it",
        category: "Server Error"
    },
    {
        code: "511",
        message: "Network Authentication Required",
        description: "The client needs to authenticate to gain network access",
        category: "Server Error"
    }
];

function HTTPErrorConverter() {
    const [errorCode, setErrorCode] = useState<string>("");
    const [result, setResult] = useState<ErrorCode | null>(null);
    const [error, setError] = useState<string>("");

    const convertErrorCode = () => {
        setError("");
        if (!errorCode.trim()) {
            setResult(null);
            return;
        }

        const foundError = httpErrorCodes.find(err => err.code === errorCode);
        if (foundError) {
            setResult(foundError);
        } else {
            setError("Invalid HTTP error code. Please enter a valid error code.");
            setResult(null);
        }
    };

    // Handle Enter key press
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            convertErrorCode();
        }
    };

    return (
        <div className="tool">
            <h2>HTTP Error Code Converter</h2>
            <div className="content">
                <div className="http-error-converter">
                    <div className="http-error-converter__input-section">
                        <div className="http-error-converter__input-container">
                            <input
                                type="text"
                                value={errorCode}
                                onChange={(e) => {
                                    setErrorCode(e.target.value);
                                    if (!e.target.value.trim()) {
                                        setResult(null);
                                    }
                                }}
                                onKeyPress={handleKeyPress}
                                placeholder="Enter HTTP error code (e.g., 404)"
                                className="http-error-converter__input"
                            />
                            <button 
                                onClick={() => setErrorCode("")}
                                className="http-error-converter__clear-button"
                            >
                                Clear
                            </button>
                        </div>

                        <button 
                            onClick={convertErrorCode}
                            className="http-error-converter__convert-button"
                        >
                            Convert
                        </button>
                    </div>

                    <div className="http-error-converter__results">
                        {error && <p className="error-message">{error}</p>}
                        {result && (
                            <>
                                <div>
                                    <p><strong>Status Code:</strong> {result.code}</p>
                                </div>
                                <div>
                                    <p><strong>Category:</strong> {result.category}</p>
                                </div>
                                <div>
                                    <p><strong>Message:</strong> {result.message}</p>
                                </div>
                                <div>
                                    <p><strong>Description:</strong> {result.description}</p>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default HTTPErrorConverter; 